/**
 * Feature flags — compile-time constants bundled into the frontend.
 *
 * These are hardcoded (not env-driven) to sidestep the Vite `define:` /
 * `import.meta.env` interaction which fails to inject the value when used via
 * `define: { 'import.meta.env.VITE_AI_V2': ... }`: Vite treats `import.meta.env.*`
 * as a managed namespace and only honours values actually present in a loaded
 * `.env` file — our `.env` files are gitignored, so Amplify builds them empty.
 *
 * Toggle AI_V2_ENABLED to false to ship the v1-only bundle (no chart render,
 * no FeedbackButtons, no PushInitializer, no AlertPoller, no ScheduledActions
 * panel, no ToolMetricsTab). Backend gating is independent via AI_SYSTEM_VERSION
 * on the Lambda.
 */

export const AI_V2_ENABLED = true;

/**
 * Bank reconciliation V2 — show ARIA cost / tokens / latency footer.
 *
 * Double-guarded at the call site with `user?.role === 'admin'` so end clients
 * never see the inference cost. Toggle to false to hide the footer entirely
 * (e.g. for a customer demo where the cost number would be distracting).
 *
 * Maps to `feature.aiCostVisible` in the original handoff (TASKS.md PR-CON-8).
 * VERIX has no remote feature-flag system — flags are compile-time constants.
 */
export const RECON_AI_COST_VISIBLE = true;

/**
 * VAPID public key for Web Push — pairs with verix/<env>/VAPID_PRIVATE_KEY in
 * SSM. If AI_V2_ENABLED is false this value is not read.
 */
export const VAPID_PUBLIC_KEY =
  'BEUvR3v8EjiZV7keWBEDsS7F2AdgLjtBwX295e4KhDDJNxzpauCQ2KKFJy-DK0Q2IfqvvOc_w_JugoePJdxg_2w';

/**
 * ADVISOR_MODE — Contabilidad · Modo Asesor (programa PR-AD-0…AD-11).
 *
 * Workspace de contabilidad por cliente para la vertiente asesoría (Crowe),
 * detrás de la ruta `/asesor/:companyId`. CONVIVE con la vista clásica; no la
 * sustituye.
 *
 * PR-AD-11c — FLIP a `true` (2026-06-09). El programa PR-AD-0…AD-11b dejó el
 * workspace completo (shell, Resumen, 13 ventanas, ARIA, a11y axe-limpio e i18n
 * es/en del Resumen+chrome). El backend ya concede el acceso del asesor por
 * vinculación a la firma: `companies.accounting_firm_id` → `getUserContext`
 * resuelve `effectiveRole='accountant_external'` e inyecta `ACCOUNTANT_EXTERNAL_PERMS`
 * (incluye `accounting.view`) — verificado en verix-api dev; cero cambio backend.
 * El dispatcher es `god_mode && isAdvisorModeEnabled(search)`, así que SOLO los
 * asesores en god_mode lo ven; el resto sigue en la vista clásica. **Rollback =
 * volver a `false`** (1 línea + deploy FE).
 *
 * Gate operativo de go-live (NO de código): empresa cliente del piloto vinculada
 * a la firma Crowe (`accounting_firm_id`, vía invitación) + smoke STG con flag ON.
 *
 * Preview histórico: `isAdvisorModeEnabled(search)` permitía `?advisor=1` solo en
 * DEV; con el flag en `true` ya no hace falta.
 */
export const ADVISOR_MODE = true;

/**
 * Resuelve si el Modo Asesor está activo para esta navegación.
 * @param {string|URLSearchParams=} search querystring actual (de useLocation)
 * @returns {boolean}
 */
export function isAdvisorModeEnabled(search) {
  if (ADVISOR_MODE) return true;
  // Override de preview SOLO en DEV — nunca expone el stub en prod.
  if (import.meta.env?.DEV && search != null) {
    const params = typeof search === 'string' ? new URLSearchParams(search) : search;
    return params.has('advisor');
  }
  return false;
}

/**
 * INVOICES_CLEAN_LIST — limpieza visual de la lista de facturas (carril F,
 * programa «Facturación limpia», PR-1…PR-10; mock `Facturacion Limpia.html`).
 *
 * Con `false` la pantalla es EXACTAMENTE la de hoy: cada PR del programa añade
 * su rama detrás de este flag y deja intacta la de siempre (lo vigila el
 * snapshot de `FacturasPage` con el flag apagado). Se enciende en el PR-7; el
 * código viejo se queda una release por si hay que volver atrás.
 * **Rollback = volver a `false`** (1 línea + deploy FE).
 */
export const INVOICES_CLEAN_LIST = false;
